import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, Phone, Building, User, FileText, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';

interface ContactForm {
  name: string;
  mobile: string;
  email: string;
  company: string;
  need_document_ai_for: string;
}

const schema = yup.object({
  name: yup.string().required('Name is required').min(1, 'Name must be at least 1 character'),
  mobile: yup.string().required('Mobile number is required').min(1, 'Mobile number is required'),
  email: yup.string().required('Email is required').email('Please enter a valid email address'),
  company: yup.string().required('Company name is required').min(1, 'Company name is required'),
  need_document_ai_for: yup
    .string()
    .required('Please describe your needs')
    .min(200, 'Please provide at least 200 characters describing your needs'),
});

const ContactUs: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactForm>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);
    try {
      await apiService.submitContact(data);
      toast.success('Thank you! We\'ll contact you soon for evaluation access.');
      navigate('/home');
    } catch (error: any) {
      const message = error.response?.data?.detail || error.message || 'Failed to submit contact form';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <PublicHeader />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 px-4">Contact Us for Evaluation Access</h1>
            <p className="text-sm sm:text-base text-gray-600 px-4">
              Fill out the form below and we'll get back to you soon to discuss your Document AI needs.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Input
                {...register('name')}
                type="text"
                label="Full Name"
                icon={User}
                error={errors.name?.message}
                placeholder="Enter your full name"
                required
              />

              <Input
                {...register('mobile')}
                type="tel"
                label="Mobile Number"
                icon={Phone}
                error={errors.mobile?.message}
                placeholder="Enter your mobile number"
                required
              />
            </div>

            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Input
                {...register('email')}
                type="email"
                label="Email Address"
                icon={Mail}
                error={errors.email?.message}
                placeholder="Enter your email address"
                required
              />

              <Input
                {...register('company')}
                type="text"
                label="Company/Organization"
                icon={Building}
                error={errors.company?.message}
                placeholder="Enter your company name"
                required
              />
            </div>

            <div>
              <label htmlFor="need_document_ai_for" className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-1" />
                Need Document AI For <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('need_document_ai_for')}
                id="need_document_ai_for"
                rows={8}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.need_document_ai_for ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Please describe in detail (minimum 200 characters) what you need Document AI for. For example: What industry are you in? What types of documents do you work with? What challenges are you facing? What would you like to achieve with Document AI?"
              />
              {errors.need_document_ai_for && (
                <p className="mt-1 text-sm text-red-600">{errors.need_document_ai_for.message}</p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Minimum 200 characters required. Please provide detailed information about your use case.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4">
              <Link
                to="/home"
                className="text-center sm:text-left text-gray-600 hover:text-gray-900 font-medium py-2"
              >
                Cancel
              </Link>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                className="w-full sm:w-auto inline-flex items-center justify-center"
              >
                <Send className="h-4 w-4 mr-2" />
                Submit Request
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 sm:mt-8 bg-primary-50 border border-primary-200 rounded-lg p-4 sm:p-6 mx-4 sm:mx-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">What Happens Next?</h3>
          <ul className="space-y-2 text-sm sm:text-base text-gray-600">
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">✓</span>
              <span>We'll review your request within 24-48 hours</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">✓</span>
              <span>Our team will contact you to discuss your specific needs</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">✓</span>
              <span>We'll provide evaluation access to our Document AI platform</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-600 mr-2">✓</span>
              <span>We'll discuss private LLM setup options if needed</span>
            </li>
          </ul>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default ContactUs;

